import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { getMappedChannelNames } from "@/shared/helpers/getMappedChannelNames";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { ChannelDataWithSourceAndTotal } from "@/shared/types";
import { axiosApi } from "@/Utils";

const getFormatsROIsV3 = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  formats = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle,
  geographies,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  startDate?: string | undefined;
  endDate?: string | undefined;
  formats?: string[] | undefined;
  geographies?: string[] | undefined;
}): Promise<{
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: {
    totalSpend: number;
    totalRevenue: number;
    totalRoi: number;
  };
}> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    formats,
    graphView,
    tableView,
    geoViewToggle,
    businessUnitToggle,
    geographies,
  };

  return axiosApi.get("/api/v2/formats", { params: payload, baseURL: INSIGHTS_BACKEND_URL });
};

const _getFormatsROIsNest = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  formats = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle,
  geographies,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  startDate?: string | undefined;
  endDate?: string | undefined;
  formats?: string[] | undefined;
  geographies?: string[] | undefined;
}): Promise<{
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: {
    totalSpend: number;
    totalRevenue: number;
    totalRoi: number;
  };
}> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (graphView) params.append("graphView", graphView);
  if (tableView) params.append("tableView", tableView);
  if (geoViewToggle) params.append("geoViewToggle", geoViewToggle);
  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (formats && formats.length > 0) {
    formats.forEach((format) => {
      params.append("formats", format);
    });
  }

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  return axiosApi.get(`/api/formats?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

export const useFormatsROIs = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  formats = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle,
  config,
  geographies,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  startDate: string;
  endDate: string;
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  formats?: string[];
  config?: QueryConfig<typeof getFormatsROIsV3 | typeof _getFormatsROIsNest>;
  geographies?: string[];
}) => {
  const endpointType = useModuleEndpoint("formats");

  const queryKey = useMemo(
    () => [
      "formats-roi-v3",
      brand,
      startDate,
      endDate,
      formats,
      graphView,
      tableView,
      geoViewToggle,
      businessUnitToggle,
      geographies,
      channels,
      product,
      category,
      endpointType,
    ],
    [
      brand,
      startDate,
      endDate,
      formats,
      graphView,
      tableView,
      geoViewToggle,
      businessUnitToggle,
      geographies,
      channels,
      product,
      category,
      endpointType,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getFormatsROIsNest({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        formats,
        graphView,
        tableView,
        geoViewToggle,
        businessUnitToggle,
        geographies,
      });
    } else {
      return getFormatsROIsV3({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        formats,
        graphView,
        tableView,
        geoViewToggle,
        businessUnitToggle,
        geographies,
      });
    }
  }, [
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    formats,
    graphView,
    tableView,
    geoViewToggle,
    businessUnitToggle,
    geographies,
    endpointType,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
    select: (data) => ({
      ...data,
      tableView: getMappedChannelNames(data.tableView),
      chartView: getMappedChannelNames(data.chartView),
    }),
  });
};
